import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';

/**
 * How the confirm button is drawn: red by default, because nearly every act behind these dialogs
 * destroys something.
 *
 * **`default` is for the act that is heavy rather than destructive**, and the one that asked for it
 * is the spreadsheet import: it writes hundreds of records, which is why the number is typed out, but
 * it removes nothing — so drawing it in the colour of an erasure would teach an administrator that red
 * means "a lot" rather than "gone".
 */
type ConfirmTone = 'destructive' | 'default';

/**
 * The dialog in front of every irreversible act in this product.
 *
 * Six things use it: purging a retired field's values, withdrawing a box of bands, withdrawing one
 * band, withdrawing an API key, arming a restore, destroying every retained archive, and erasing a
 * person. It sits above `features/` rather than inside one of them because the last of those is on
 * the record screen and the rest are in administration, and a shared component that lived in one
 * feature would be imported sideways by the other. Each one destroys something no later act can
 * recover, and each one is reached from a list where the row above it is harmless.
 *
 * **The phrase has to be TYPED, and it is not "yes".** It is the record's own short reference, the
 * batch's label, or a word the contract itself demands (`restoreRequestSchema.confirmation` is
 * `z.literal('RESTORE')`). Typing it is what makes the act impossible to complete by clicking
 * through a dialog somebody has seen fifty times: the person doing it has to have the thing in
 * front of them.
 *
 * **Nothing is sent until the phrase matches.** The confirm button is disabled and `onConfirm` is
 * not reachable, which is the negative every one of this phase's confirmation tests asserts — a
 * dialog that issued the request and let the server refuse it would have already written the audit
 * row for an attempt nobody made, into tables that admit no DELETE.
 *
 * **The comparison is exact, after trimming.** Case folding would accept `restore` for `RESTORE`,
 * which the server refuses, so the operator would be told their typing was fine and then shown a
 * validation failure; trimming alone forgives the space a copy-and-paste leaves behind.
 *
 * **The body is MOUNTED per opening rather than toggled.** The typed phrase is component state, so
 * mounting is what clears it: a phrase left over from the last time this dialog was opened, on a
 * shared terminal, would mean the second act needed no typing at all. It is the same reason
 * `features/scan/anomaly.tsx` mounts per anomaly, and it needs no effect to synchronise anything.
 */
export function TypedConfirmDialog({
  open,
  ...rest
}: Readonly<{
  open: boolean;
  title: string;
  body: string;
  phrase: string;
  confirmLabel: string;
  pending?: boolean;
  ready?: boolean;
  tone?: ConfirmTone;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}>): ReactNode {
  return open ? <ConfirmBody {...rest} /> : null;
}

function ConfirmBody({
  title,
  body,
  phrase,
  confirmLabel,
  pending = false,
  ready = true,
  tone = 'destructive',
  onConfirm,
  onCancel,
  children,
}: Readonly<{
  title: string;
  body: string;
  /** The exact text that unlocks the button. Shown to the operator so it can be typed. */
  phrase: string;
  confirmLabel: string;
  pending?: boolean;
  /**
   * Whatever else this particular act needs, as one boolean.
   *
   * The erasure needs a reason as well as the phrase, and the batch withdrawal needs one too. A
   * dialog whose button was live before those were given would call `onConfirm` and have the caller
   * silently do nothing, which is the "the field validates and nothing happens" defect in dialog
   * form: the operator presses the button, the record is still there, and nothing says why.
   */
  ready?: boolean;
  /** How the confirm button is drawn. See {@link ConfirmTone}. */
  tone?: ConfirmTone;
  onConfirm: () => void;
  onCancel: () => void;
  /** Whatever else this particular act needs: a reason picker, a manifest, a warning. */
  children?: ReactNode;
}>): ReactNode {
  const { t } = useTranslation();
  const fieldId = useId();
  const [typed, setTyped] = useState('');

  const matches = typed.trim() === phrase;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent closeLabel={t('common.close')} data-testid="typed-confirm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>

        {children}

        <div className="flex flex-col gap-2">
          <Label htmlFor={fieldId}>{t('confirm.typeToContinue', { phrase })}</Label>
          <Input
            id={fieldId}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            data-testid="confirm-phrase"
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('confirm.cancel')}
          </Button>
          <Button
            type="button"
            variant={tone}
            disabled={!matches || !ready || pending}
            data-testid="confirm-submit"
            onClick={onConfirm}
          >
            {pending ? t('confirm.working') : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The lighter sibling: a confirmation with no phrase to type.
 *
 * **It is for an act that is reversible by doing it again, and destructive enough not to be one
 * press.** Replacing somebody's wristband is the case it was written for: the pass they are holding
 * stops resolving at every door the moment it commits, which is not something to do by pressing the
 * wrong control on a record screen, but it is repaired by issuing another band rather than by a
 * restore. The typed phrase above is for the acts nothing recovers.
 *
 * Same mount-per-opening discipline and the same footer, so the two read as one family and the
 * primary action stays the LAST control in the footer, which is what every dialog in this product
 * and its screenshot tests rely on.
 */
export function ConfirmDialog({
  open,
  ...rest
}: Readonly<{
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  pending?: boolean;
  tone?: ConfirmTone;
  onConfirm: () => void;
  onCancel: () => void;
}>): ReactNode {
  return open ? <PlainConfirmBody {...rest} /> : null;
}

function PlainConfirmBody({
  title,
  body,
  confirmLabel,
  pending = false,
  tone = 'destructive',
  onConfirm,
  onCancel,
}: Readonly<{
  title: string;
  body: string;
  confirmLabel: string;
  pending?: boolean;
  tone?: ConfirmTone;
  onConfirm: () => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent closeLabel={t('common.close')} data-testid="confirm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('confirm.cancel')}
          </Button>
          <Button
            type="button"
            variant={tone}
            disabled={pending}
            data-testid="confirm-submit"
            onClick={onConfirm}
          >
            {pending ? t('confirm.working') : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
